import React, { useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router";

import { useDialogContextMenuItemsFrontendAddon } from "@/components/Dialog/DialogServer/DialogFrontendAddon/hooks";
import {
  Add20Regular,
  Checkmark20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import { useAppAccount } from "../../AppProvider/AppAccountProvider";
import { LoadingCover } from "../../LoadingCover";
import {
  GridTableTemplate,
  renderDescriptionCell,
  renderGridTableDateTimeCell,
} from "../helpers";
import styles from "../index.module.css";

import { ErrorDisplay } from "@/components/ErrorDisplay";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { FrontendAddonRaw } from "@/domain/FrontendAddon";
import { Button, Dropdown, Input, Option } from "@fluentui/react-components";
import headerStyles from "./index.module.css";

const asyncNop = async () => {};

type StatusFilter = "enable" | "disable";
const statusFilterOptions: { displayName: string; key: StatusFilter }[] = [
  {
    displayName: "有効状態",
    key: "enable",
  },
  {
    displayName: "無効状態",
    key: "disable",
  },
];

export const GridTableFrontendAddons = <T extends FrontendAddonRaw>({
  isLoadedFrontendAddons,
  frontendAddons,
  runReloadFrontendAddons,
  returnPath,
  additionalCols,
  overrideUpdatedAtRender,
  showSearch = false,
}: {
  isLoadedFrontendAddons: boolean;
  frontendAddons: T[] | null;
  runReloadFrontendAddons?: () => Promise<void>;
  returnPath: string;
  additionalCols?: {
    title: string;
    render: (frontendAddon: T) => React.ReactNode;
  }[];
  overrideUpdatedAtRender?: (frontendAddon: T) => React.ReactNode;
  // 検索欄は管理者ビューのみ (SSUI-2333)
  showSearch?: boolean;
}) => {
  const { isAADeveloperOrAbove } = useAppAccount();
  const { hasPermission } = usePermissions();
  const [statusFilter, setStatusFilter] = useState<StatusFilter | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const hasWritePermission =
    isAADeveloperOrAbove || hasPermission("frontend_addons_owned", "write");

  const {
    renderDialogsFrontendAddon,
    getContextMenuItemsEnableFrontendAddon,
    getContextMenuItemsDisableFrontendAddon,
    selectedFrontendAddonIds,
    setSelectedFrontendAddonIds,
    renderFrontendAddonsSelectionBar,
  } = useDialogContextMenuItemsFrontendAddon(
    runReloadFrontendAddons ? runReloadFrontendAddons : asyncNop,
    returnPath
  );

  const sortedFrontendAddons = useMemo(
    () =>
      frontendAddons
        ? [...frontendAddons].sort((a, b) =>
            b.created_at.localeCompare(a.created_at)
          )
        : undefined,
    [frontendAddons]
  );

  const filterBySearchQuery = useCallback(
    (addons: T[]) => {
      if (!searchQuery) return addons;
      const query = searchQuery.toLowerCase();
      const selectedIds = new Set(selectedFrontendAddonIds);
      return addons.filter(
        (addon) =>
          selectedIds.has(addon.id) ||
          addon.name.toLowerCase().includes(query) ||
          addon.id.toLowerCase().includes(query) ||
          (addon.description ?? "").toLowerCase().includes(query)
      );
    },
    [searchQuery, selectedFrontendAddonIds]
  );

  const displayFrontendAddons = useMemo(
    () =>
      filterBySearchQuery(
        (sortedFrontendAddons ?? []).filter((frontendAddon) => {
          if (statusFilter === null) return true;
          return statusFilter === "enable"
            ? !frontendAddon.is_disabled
            : frontendAddon.is_disabled;
        })
      ),
    [sortedFrontendAddons, statusFilter, filterBySearchQuery]
  );

  const selectStatusFilter = useCallback(
    (filter: StatusFilter | null) => {
      if (filter === statusFilter) return;
      setStatusFilter(filter);
      setSelectedFrontendAddonIds([]);
    },
    [statusFilter, setSelectedFrontendAddonIds]
  );

  const renderHeader = useCallback(() => {
    const selectedFilter = statusFilterOptions.find(
      ({ key }) => key === statusFilter
    );
    return (
      <div className={styles.header}>
        <div className={headerStyles.headerRow}>
          <div className={headerStyles.filterArea}>
            <Dropdown
              className={headerStyles.statusFilterDropdown}
              aria-label="ステータスで絞り込む"
              placeholder="ステータス"
              value={selectedFilter?.displayName ?? ""}
              selectedOptions={statusFilter ? [statusFilter] : []}
              onOptionSelect={(_e, data) => {
                selectStatusFilter((data.optionValue as StatusFilter) ?? null);
              }}
            >
              {statusFilterOptions.map(({ key, displayName }) => (
                <Option key={key} value={key}>
                  {displayName}
                </Option>
              ))}
            </Dropdown>
            <Button
              appearance="transparent"
              disabled={statusFilter === null}
              onClick={() => selectStatusFilter(null)}
            >
              クリア
            </Button>
          </div>
          <div className={styles.rightEndContent}>
            {showSearch && (
              <Input
                className={headerStyles.searchInput}
                aria-label="アドオン名・概要・IDで絞り込む"
                contentBefore={<Search20Regular />}
                value={searchQuery}
                placeholder="検索"
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            )}
            <Button
              appearance="primary"
              icon={<Add20Regular />}
              onClick={() => {
                navigate(`${returnPath}/create`);
              }}
              disabled={!hasWritePermission}
            >
              アドオンを作成
            </Button>
          </div>
        </div>
        {renderFrontendAddonsSelectionBar(displayFrontendAddons)}
      </div>
    );
  }, [
    hasWritePermission,
    statusFilter,
    selectStatusFilter,
    displayFrontendAddons,
    renderFrontendAddonsSelectionBar,
    searchQuery,
    showSearch,
    navigate,
    returnPath,
  ]);

  const renderFrontendAddonNameId = (
    frontendAddonName: FrontendAddonRaw["name"],
    frontendAddonId: FrontendAddonRaw["id"]
  ) => {
    return (
      <div className={styles.doubleRows}>
        <div className={styles.doubleRowsTop}>{frontendAddonName}</div>
        <div className={styles.doubleRowsUnder}>ID : {frontendAddonId}</div>
      </div>
    );
  };

  const tbodyRowCells = displayFrontendAddons.map((frontendAddon) => {
    const { id, name, description } = frontendAddon;
    const rowCells = [
      renderFrontendAddonNameId(name, id),
      renderDescriptionCell(description),
      ...(additionalCols || []).map((c) => c.render(frontendAddon)),
      frontendAddon.is_disabled ? null : <Checkmark20Regular />,
      overrideUpdatedAtRender
        ? overrideUpdatedAtRender(frontendAddon)
        : renderGridTableDateTimeCell(frontendAddon.updated_at),
    ];
    const to = `${returnPath}/${id}/frontend_addon_edit`;

    return {
      id,
      rowCells,
      to,
      contextMenuItems: frontendAddon.is_disabled
        ? getContextMenuItemsDisableFrontendAddon(frontendAddon)
        : getContextMenuItemsEnableFrontendAddon(frontendAddon),
    };
  });

  const theadCells: string[] = useMemo(
    () => [
      "アドオン名",
      "概要",
      ...(additionalCols ?? []).map((c) => c.title),
      "有効",
      "更新時刻",
    ],
    [additionalCols]
  );
  const cellsWidths: number[] = useMemo(
    () => [300, 300, ...(additionalCols ?? []).map(() => 180), 80, 180],
    [additionalCols]
  );

  if (!isLoadedFrontendAddons) return <LoadingCover />;
  if (!sortedFrontendAddons) {
    return (
      <ErrorDisplay
        errorDisplayType="permissionError"
        missingPermissions={["frontend_addons_owned:read"]}
      />
    );
  }
  return (
    <>
      <GridTableTemplate
        headerElement={renderHeader()}
        theadCells={theadCells}
        tbodyRowsCells={tbodyRowCells}
        cellsWidth={cellsWidths}
        checkedIds={selectedFrontendAddonIds}
        setCheckedIds={setSelectedFrontendAddonIds}
        enableContextMenuColumn
        fixedColumnCounts={{
          left: 2,
          right: 1,
        }}
      />
      {/* ダイアログの対象を選択バーと同じ表示中の一覧に揃える */}
      {renderDialogsFrontendAddon(displayFrontendAddons)}
    </>
  );
};
